import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  EltsInput, PvRiskInput, EtRiskInput,
  DipssInput, DipssPlusInput, Mipss70Input, Mpn10Input,
  GfmInput, MayoInput, CpssMolInput, BlastInput, BlastMolInput,
  CmmlRiskModelId,
} from '../types';

// ── CML Diagnosis state ──
export interface CmlDiagnosisState {
  blasts: string;
  basophils: string;
  plateletsLow: boolean | null;
  extramedullary: boolean | null;
  lymphoblasts: boolean | null;
  /** Major-route ACA emerging on treatment — an ELN accelerated-phase criterion */
  acaOnTreatment: boolean | null;
}

const defaultCmlDiagnosis: CmlDiagnosisState = {
  blasts: '',
  basophils: '',
  plateletsLow: null,
  extramedullary: null,
  lymphoblasts: null,
  acaOnTreatment: null,
};

// ── CML Treatment Response state ──
export interface TreatmentResponseState {
  timePoint: string;
  bcrAblInput: string;
}

const defaultTreatmentResponse: TreatmentResponseState = {
  timePoint: '',
  bcrAblInput: '',
};

// ── Default values ──
const defaultElts: EltsInput = { age: '', spleenSize: '', blasts: '', plateletCount: '' };
const defaultPvRisk: PvRiskInput = { ageOver60: null, priorThrombosis: null };
const defaultEtRisk: EtRiskInput = { age: '', jak2Mutation: null, priorThrombosis: null };

// ── MF defaults ──
const defaultDipss: DipssInput = { age: '', wbc: '', hemoglobin: '', blasts: '', constitutionalSymptoms: null };
const defaultDipssPlus: DipssPlusInput = { plateletsLow: null, transfusionNeed: null, unfavorableKaryotype: null };
const defaultMipss70: Mipss70Input = { sex: null, hemoglobin: '', blasts: '', constitutionalSymptoms: null, calrType1: null, hmrCount: '', karyotype: '' };
const defaultMpn10: Mpn10Input = { symptoms: Array(10).fill('') };

// ── CMML defaults ──
const defaultGfm: GfmInput = { age: '', wbc: '', hemoglobin: '', sex: null, plateletsLow: null, asxl1: null };
const defaultMayo: MayoInput = { hemoglobin: '', plateletsLow: null, amc: '', imcsPresent: null, asxl1Frameshift: null };
const defaultCpssMol: CpssMolInput = { wbc: '', bmBlasts: '', runx1: null, asxl1: null, nras: null, setbp1: null, cpssKaryotype: '', transfusionDependent: null };
const defaultBlast: BlastInput = { sex: null, hemoglobin: '', blasts: '', wbc: '', transfusionNeed: null };
const defaultBlastMol: BlastMolInput = { sex: null, hemoglobin: '', blasts: '', wbc: '', transfusionNeed: null, molecularRisk: '' };

// ── Diagnostic criteria selections (shared by PV/ET/MF/CMML) ──
export type CriteriaSelections = Record<string, boolean | null>;

// ── Full state shape ──
export interface AllUserInput {
  // CML
  cmlDiagnosis: CmlDiagnosisState;
  elts: EltsInput;
  treatmentResponse: TreatmentResponseState;
  // PV
  pvDiagnosisCriteria: CriteriaSelections;
  pvRisk: PvRiskInput;
  // ET
  etDiagnosisCriteria: CriteriaSelections;
  etRisk: EtRiskInput;
  // MF
  mfOvertCriteria: CriteriaSelections;
  mfPrefibCriteria: CriteriaSelections;
  mfPostPvCriteria: CriteriaSelections;
  mfPostEtCriteria: CriteriaSelections;
  selectedMfDiagnosis: 'overt' | 'prefib' | 'postPv' | 'postEt';
  selectedMfRiskModel: 'dipss' | 'mipss70';
  dipss: DipssInput;
  dipssPlus: DipssPlusInput;
  mipss70: Mipss70Input;
  mpn10: Mpn10Input;
  // CMML
  cmmlWho2022Criteria: CriteriaSelections;
  /** WHO 2022: monocytes ≥1 × 10⁹/L? Decides which supporting criteria are needed */
  cmmlWhoAmcAtLeast1: boolean | null;
  cmmlIcc2022Criteria: CriteriaSelections;
  selectedCmmlRiskModel: CmmlRiskModelId;
  gfm: GfmInput;
  mayo: MayoInput;
  cpssMol: CpssMolInput;
  blast: BlastInput;
  blastMol: BlastMolInput;
}

const defaultState: AllUserInput = {
  cmlDiagnosis: defaultCmlDiagnosis,
  elts: defaultElts,
  treatmentResponse: defaultTreatmentResponse,
  pvDiagnosisCriteria: {},
  pvRisk: defaultPvRisk,
  etDiagnosisCriteria: {},
  etRisk: defaultEtRisk,
  // MF
  mfOvertCriteria: {},
  mfPrefibCriteria: {},
  mfPostPvCriteria: {},
  mfPostEtCriteria: {},
  selectedMfDiagnosis: 'overt' as const,
  selectedMfRiskModel: 'dipss',
  dipss: defaultDipss,
  dipssPlus: defaultDipssPlus,
  mipss70: defaultMipss70,
  mpn10: defaultMpn10,
  // CMML
  cmmlWho2022Criteria: {},
  cmmlWhoAmcAtLeast1: null,
  cmmlIcc2022Criteria: {},
  selectedCmmlRiskModel: 'gfm',
  gfm: defaultGfm,
  mayo: defaultMayo,
  cpssMol: defaultCpssMol,
  blast: defaultBlast,
  blastMol: defaultBlastMol,
};

interface UserInputContextValue {
  state: AllUserInput;
  update: <K extends keyof AllUserInput>(key: K, value: AllUserInput[K]) => void;
  resetAll: () => void;
  resetDisease: (disease: 'cml' | 'pv' | 'et' | 'mf' | 'cmml') => void;
}

const UserInputContext = createContext<UserInputContextValue | null>(null);

export const UserInputProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AllUserInput>(defaultState);

  const update = useCallback(<K extends keyof AllUserInput>(key: K, value: AllUserInput[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetAll = useCallback(() => {
    setState(defaultState);
  }, []);

  const resetDisease = useCallback((disease: 'cml' | 'pv' | 'et' | 'mf' | 'cmml') => {
    setState((prev) => {
      switch (disease) {
        case 'cml':
          return {
            ...prev,
            cmlDiagnosis: defaultCmlDiagnosis,
            elts: defaultElts,
            treatmentResponse: defaultTreatmentResponse,
          };
        case 'pv':
          return {
            ...prev,
            pvDiagnosisCriteria: {},
            pvRisk: defaultPvRisk,
          };
        case 'et':
          return {
            ...prev,
            etDiagnosisCriteria: {},
            etRisk: defaultEtRisk,
          };
        case 'mf':
          return {
            ...prev,
            mfOvertCriteria: {},
            mfPrefibCriteria: {},
            mfPostPvCriteria: {},
            mfPostEtCriteria: {},
            selectedMfDiagnosis: 'overt' as const,
            selectedMfRiskModel: 'dipss' as const,
            dipss: defaultDipss,
            dipssPlus: defaultDipssPlus,
            mipss70: defaultMipss70,
            mpn10: defaultMpn10,
          };
        case 'cmml':
          return {
            ...prev,
            cmmlWho2022Criteria: {},
            cmmlWhoAmcAtLeast1: null,
            cmmlIcc2022Criteria: {},
            selectedCmmlRiskModel: 'gfm' as CmmlRiskModelId,
            gfm: defaultGfm,
            mayo: defaultMayo,
            cpssMol: defaultCpssMol,
            blast: defaultBlast,
            blastMol: defaultBlastMol,
          };
        default:
          return prev;
      }
    });
  }, []);

  return (
    <UserInputContext.Provider value={{ state, update, resetAll, resetDisease }}>
      {children}
    </UserInputContext.Provider>
  );
};

export const useUserInput = (): UserInputContextValue => {
  const ctx = useContext(UserInputContext);
  if (!ctx) throw new Error('useUserInput must be used within UserInputProvider');
  return ctx;
};
